/**
 * CR324: React hook wrapping assembleDealCardPreview for memoized usage.
 *
 * Provides the assembled preview config, hash, and source map
 * with proper React memoization to prevent unnecessary re-computations.
 */

import { useMemo } from "react";
import {
  assembleDealCardPreview,
  type AssemblePreviewInput,
  type AssemblePreviewOutput,
  type PreviewMode,
  type SavedTemplateInput,
} from "@/lib/deals/preview/assembleDealCardPreview";

export type { PreviewMode } from "@/lib/deals/preview/assembleDealCardPreview";

export interface UsePreviewAssemblerInput {
  dealTypeKey: string;
  variantKey: string;
  previewMode: PreviewMode;

  /** Raw saved template from DB */
  savedTemplate: SavedTemplateInput;

  /** Template editor draft (full override object, not a delta) */
  templateDraftPatch?: Record<string, unknown> | null;

  /** Saved component-level overrides from DB */
  savedComponents?: Record<string, unknown> | null;

  /** Components Studio draft */
  componentDraftPatch?: Record<string, unknown> | null;

  /** Optional selected sample deal ID for diagnostics */
  selectedDealId?: string | null;
}

export interface UsePreviewAssemblerResult extends AssemblePreviewOutput {
  /** Serialized diagnostics JSON for copy-to-clipboard */
  diagnosticsJson: string;
}

/**
 * Memoized wrapper around assembleDealCardPreview.
 *
 * Recalculates only when inputs change (via useMemo deps).
 */
export function usePreviewAssembler(input: UsePreviewAssemblerInput): UsePreviewAssemblerResult {
  const {
    dealTypeKey,
    variantKey,
    previewMode,
    savedTemplate,
    templateDraftPatch,
    savedComponents,
    componentDraftPatch,
    selectedDealId,
  } = input;

  const assemblerOutput = useMemo<AssemblePreviewOutput>(() => {
    return assembleDealCardPreview({
      dealTypeKey,
      variantKey,
      previewMode,
      savedTemplate,
      templateDraftPatch,
      savedComponents,
      componentDraftPatch,
    });
  }, [
    dealTypeKey,
    variantKey,
    previewMode,
    savedTemplate,
    templateDraftPatch,
    savedComponents,
    componentDraftPatch,
  ]);

  const diagnosticsJson = useMemo(() => {
    return JSON.stringify(
      {
        dealTypeKey,
        variantKey,
        previewMode,
        renderSignatureHash: assemblerOutput.renderSignatureHash,
        sourceMap: assemblerOutput.sourceMap,
        selectedDealId: selectedDealId ?? null,
      },
      null,
      2
    );
  }, [dealTypeKey, variantKey, previewMode, assemblerOutput, selectedDealId]);

  return {
    ...assemblerOutput,
    diagnosticsJson,
  };
}
